
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { submitToZoho } from "@/utils/secureFormSubmission";

const SecurityChallengeBanner = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent, formType: string) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const success = await submitToZoho({
        ...formData,
        formType
      });

      if (success) {
        toast.success("Thank you! Our security team will contact you shortly.");
        setFormData({ firstName: '', lastName: '', email: '', company: '', message: '' });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error("Request failed. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section 
      className="py-12 md:py-16 relative overflow-hidden bg-white"
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#8932c8]/5 via-transparent to-[#8932c8]/5"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-6 md:mb-8">
            Ready to Transform Your Security Operations with 
            <span className="text-[#8932c8]"> AI-Powered Intelligence?</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
            Join leading organizations who have already revolutionized their cybersecurity 
            with Spharaka's advanced XDR platform. Experience the power of autonomous 
            threat detection and response.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Dialog>
              <DialogTrigger asChild>
                <button className="bg-[#8932c8] hover:bg-[#7a2cb3] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Explore Spharaka Platform
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">Explore Spharaka XDR™</DialogTitle>
                </DialogHeader>
                <form onSubmit={(e) => handleSubmit(e, "Platform Exploration Request")} className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                  <Textarea
                    name="message"
                    placeholder="What security challenges are you facing?"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Send Platform Information"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <button className="border-2 border-[#8932c8] text-[#8932c8] hover:bg-[#8932c8] hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Schedule Demo
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">Schedule Your Demo</DialogTitle>
                </DialogHeader>
                <form onSubmit={(e) => handleSubmit(e, "Demo Request - Security Challenge")} className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                  <Textarea
                    name="message"
                    placeholder="Tell us about your current security setup and demo preferences"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Scheduling..." : "Schedule My Demo"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecurityChallengeBanner;
